/**
 * Security (email, two-step enrolment, recovery codes, password) and Sessions
 * (devices, sign out everywhere). Strings from user-management §2.11.
 */
import { ApiErrorCode, type AuthSessionInfo } from '@neosocial/protocol';
import qrcode from 'qrcode-generator';
import { useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n';
import { api, isApiRequestError, tokens } from '../lib/api';
import { osName } from '../lib/device';
import { askStepUp, challengeOf, needsStepUp } from '../mod/actions';
import { updateData, useData } from '../store/data';
import { signOut } from '../store/session';
import { Btn, Icon, Switch } from '../ui/kit';
import { confirmDialog, promptDialog, toast } from '../ui/overlay';
import { reportError } from '../hub/social';
import { isLeakedPassword } from '../auth/leaked';
import { PasswordInput } from '../auth/PasswordInput';
import { SetGroup, SetRow } from './Settings';
import { EmailSettings } from '../account/Email';
import { AccountRemoval } from '../account/Removal';

/**
 * Runs an account action that may need a recent "confirm it's you"
 * (step-up). On the server's refusal it opens the step-up dialog, which
 * asks for what the server needs (the password, or with two-step on a code
 * from the app or a recovery code) and stays open on a wrong answer; then
 * the action runs again. `second_factor` actions (new recovery codes, a
 * password change with two-step on) ask for a two-step code when two-step
 * is on, the others the password; if the server still wants more (two-step
 * turned on elsewhere), it asks for the code. Resolves null when the person
 * cancels. Live bugs 2026-10-28: signing out other devices only said
 * "Confirm it's you first" after 10 minutes, and with two-step on, new
 * codes and a password change could never succeed (ST-B01..B03, AA QA).
 */
export async function withStepUp<T>(
  fn: () => Promise<T>,
  need: 'password' | 'second_factor' = 'password',
): Promise<T | null> {
  for (let asked = 0; ; asked++) {
    try {
      return await fn();
    } catch (e) {
      if (!needsStepUp(e) || asked >= 2) throw e;
      const level = need === 'second_factor' || asked > 0 ? 'second_factor' : 'password';
      const challenge = challengeOf(e, { reason: 'account', level, max_age_s: 600 });
      if (!(await askStepUp(challenge))) return null;
    }
  }
}

export function QrCode({ text, size = 176 }: { text: string; size?: number }) {
  const cells = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(text);
    q.make();
    const n = q.getModuleCount();
    const out: Array<[number, number]> = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (q.isDark(r, c)) out.push([c, r]);
    return { n, out };
  }, [text]);
  const pad = 2;
  const dim = cells.n + pad * 2;
  return (
    <svg
      className="ns-qr"
      width={size}
      height={size}
      viewBox={`0 0 ${dim} ${dim}`}
      role="img"
      aria-label="QR"
      shapeRendering="crispEdges"
    >
      <rect width={dim} height={dim} fill="#fff" />
      {cells.out.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x + pad} y={y + pad} width={1} height={1} fill="#000" />
      ))}
    </svg>
  );
}

export function RecoveryCodes({
  codes,
  onDone,
  mustConfirm,
}: {
  codes: string[];
  onDone(): void;
  mustConfirm: boolean;
}) {
  const { t } = useT();
  const [saved, setSaved] = useState(false);
  return (
    <div className="ns-codes">
      <p>{t('auth.codes.body')}</p>
      <ol className="ns-codes-list" dir="ltr">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <p className="ns-help">{t('auth.codes.where')}</p>
      <div className="ns-row-btns">
        <Btn
          size="sm"
          icon="copy"
          onClick={() =>
            void navigator.clipboard
              ?.writeText(codes.join('\n'))
              .then(() => toast(t('c.copied'), { icon: 'check' }))
          }
        >
          {t('auth.codes.copy')}
        </Btn>
      </div>
      {mustConfirm ? (
        <label className="ns-check">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
          <span>{t('auth.codes.confirm')}</span>
        </label>
      ) : null}
      <div className="ns-row-btns end">
        <Btn kind="primary" size="sm" disabled={mustConfirm && !saved} onClick={onDone}>
          {t('c.done')}
        </Btn>
      </div>
    </div>
  );
}

function TwoStep() {
  const { t } = useT();
  const me = useData((d) => d.me);
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  if (!me) return null;
  if (codes)
    return (
      <SetGroup title={t('auth.codes.title')} form>
        <RecoveryCodes codes={codes} mustConfirm onDone={() => setCodes(null)} />
      </SetGroup>
    );
  if (me.mfa_enabled) {
    return (
      <SetGroup title={t('auth.mfa.title')}>
        <SetRow label={t('security.2fa.on')} sub={t('security.2fa.off.warn')}>
          <Btn
            size="sm"
            kind="danger"
            onClick={async () => {
              const c = await promptDialog({
                title: t('ns.mfa.disable'),
                body: t('security.2fa.off.warn'),
                inputLabel: t('ns.mfa.codeOrRecovery'),
                icon: 'shield',
              });
              if (!c) return;
              try {
                await api.me.totpDisable({ code: c.trim() });
                updateData((d) => (d.me ? { ...d, me: { ...d.me, mfa_enabled: false } } : d));
                toast(t('ns.mfa.disabled'), { icon: 'shield' });
              } catch (e) {
                if (isApiRequestError(e, ApiErrorCode.INVALID_MFA_CODE))
                  toast(t('auth.mfa.wrong'), { tone: 'danger', icon: 'alert' });
                else reportError(e);
              }
            }}
          >
            {t('ns.mfa.disable')}
          </Btn>
        </SetRow>
        <SetRow label={t('security.codes.regen')}>
          <Btn
            size="sm"
            onClick={async () => {
              try {
                // two-step is on: a code from the app or a recovery code
                const r = await withStepUp(() => api.me.regenerateCodes(), 'second_factor');
                if (r) setCodes(r.recovery_codes);
              } catch (e) {
                reportError(e);
              }
            }}
          >
            {t('ns.mfa.newCodes')}
          </Btn>
        </SetRow>
      </SetGroup>
    );
  }
  if (!setup) {
    return (
      <SetGroup title={t('auth.mfa.title')}>
        <SetRow label={t('ns.mfa.off')} sub={t('ns.mfa.offSub')}>
          <Btn
            size="sm"
            kind="primary"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await withStepUp(() => api.me.totpSetup(), 'password');
                if (r) setSetup({ secret: r.secret, uri: r.otpauth_uri });
              } catch (e) {
                reportError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('ns.mfa.turnOn')}
          </Btn>
        </SetRow>
      </SetGroup>
    );
  }
  return (
    <SetGroup title={t('auth.mfa.title')} form>
      <div className="ns-mfa-setup">
        <QrCode text={setup.uri} />
        <div className="nx-set-form">
          <p>{t('security.2fa.setup')}</p>
          <p className="ns-help">
            {t('ns.mfa.manual')} <code dir="ltr">{setup.secret}</code>
          </p>
          <form
            className="ns-field"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setErr(null);
              try {
                // the 10-minute window may have ended while the QR code was open
                const r = await withStepUp(
                  () =>
                    api.me.totpEnable({
                      secret: setup.secret,
                      code: code.replace(/\s/g, ''),
                    }),
                  'password',
                );
                if (!r) return;
                updateData((d) => (d.me ? { ...d, me: { ...d.me, mfa_enabled: true } } : d));
                setSetup(null);
                setCodes(r.recovery_codes);
              } catch (er) {
                if (isApiRequestError(er, ApiErrorCode.INVALID_MFA_CODE))
                  setErr(t('auth.mfa.wrong'));
                else reportError(er);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              <span className="ns-label">{t('auth.mfa.totp')}</span>
              <input
                className="nx-input ns-code-in"
                inputMode="numeric"
                autoComplete="one-time-code"
                dir="ltr"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-invalid={!!err || undefined}
              />
            </label>
            {err ? (
              <p className="ns-err" role="alert">
                {err}
              </p>
            ) : null}
            <div className="ns-row-btns">
              <Btn size="sm" onClick={() => setSetup(null)}>
                {t('c.cancel')}
              </Btn>
              <Btn
                kind="primary"
                size="sm"
                type="submit"
                disabled={code.replace(/\s/g, '').length < 6}
                busy={busy}
              >
                {t('ns.mfa.enable')}
              </Btn>
            </div>
          </form>
        </div>
      </div>
    </SetGroup>
  );
}

function PasswordChange() {
  const { t } = useT();
  const mfa = useData((d) => !!d.me?.mfa_enabled);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [others, setOthers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <SetGroup title={t('ns.pw.title')} form>
      <form
        className="nx-set-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          if (next.length < 8) {
            setErr(t('auth.password.help'));
            return;
          }
          setBusy(true);
          try {
            // with two-step on, the password alone is not enough: the dialog asks for a code
            const r = await withStepUp(
              () =>
                api.me.password({
                  current_password: cur,
                  new_password: next,
                  sign_out_others: others,
                }),
              mfa ? 'second_factor' : 'password',
            );
            if (!r) return;
            tokens.save(r);
            setCur('');
            setNext('');
            toast(t('ns.pw.changed'), { icon: 'check', tone: 'ok' });
          } catch (er) {
            if (isApiRequestError(er, ApiErrorCode.INVALID_CREDENTIALS)) setErr(t('ns.pw.wrong'));
            else if (isLeakedPassword(er, 'new_password')) setErr(t('auth.password.breached'));
            else if (isApiRequestError(er) && er.fieldCodes().new_password)
              setErr(t('auth.password.help'));
            else reportError(er);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="ns-field">
          <span className="ns-label">{t('ns.pw.current')}</span>
          <PasswordInput
            autoComplete="current-password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            aria-invalid={err === t('ns.pw.wrong') || undefined}
          />
        </label>
        <label className="ns-field">
          <span className="ns-label">{t('ns.pw.new')}</span>
          <PasswordInput
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            aria-invalid={(!!err && err !== t('ns.pw.wrong')) || undefined}
          />
          <span className="ns-help">{t('auth.password.help')}</span>
        </label>
        <div className="ns-row">
          <span>{t('security.password.signOutOthers')}</span>
          <Switch
            checked={others}
            onChange={setOthers}
            label={t('security.password.signOutOthers')}
          />
        </div>
        {err ? (
          <p className="ns-err" role="alert">
            {err}
          </p>
        ) : null}
        <div className="ns-row-btns end">
          <Btn kind="primary" size="sm" type="submit" disabled={!cur || !next} busy={busy}>
            {t('ns.pw.change')}
          </Btn>
        </div>
      </form>
    </SetGroup>
  );
}

export function SecuritySection() {
  const { t } = useT();
  return (
    <>
      <h2>{t('security.title')}</h2>
      <EmailSettings />
      <TwoStep />
      <PasswordChange />
      {/* Disable account, Delete account (2026-10-29) */}
      <AccountRemoval />
    </>
  );
}

/** "Chrome on Windows"; older sessions were saved with the raw platform ("Win32" → "Windows"). */
export function sessionTitle(
  s: Pick<AuthSessionInfo, 'device_name' | 'os' | 'client'>,
  t: (k: string, v?: Record<string, string>) => string,
): string {
  const os = osName(s.os);
  if (s.device_name && os) return t('set.device.on', { browser: s.device_name, os });
  return s.device_name || os || t(`ns.client.${s.client}`);
}

export function SessionsSection() {
  const { t, fmt } = useT();
  const [list, setList] = useState<AuthSessionInfo[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    api.me
      .sessions()
      .then((l) => live && (setList(l), setFailed(false)))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [n]);
  const reload = () => setN((x) => x + 1);
  return (
    <>
      <h2>{t('devices.title')}</h2>
      <SetGroup>
        {failed ? (
          <p className="nx-set-note" role="alert">
            <Icon name="alert" />
            <span>{t('ns.sessions.failed')}</span>
            <Btn size="xs" onClick={reload}>
              {t('ns.msg.retry')}
            </Btn>
          </p>
        ) : !list ? (
          <p className="nx-set-note" role="status">
            <i className="nx-spin" />
            <span>{t('ns.loading')}</span>
          </p>
        ) : (
          list.map((s) => (
            <div key={s.id} className="nx-set-row">
              <div className="nx-set-rt">
                <div className="l">
                  <Icon name={s.client === 'desktop' ? 'monitor' : 'globe'} /> {sessionTitle(s, t)}
                  {s.current ? (
                    <span className="nx-chip ns-this" data-tone="ok">
                      {t('devices.this')}
                    </span>
                  ) : null}
                </div>
                <div className="s">
                  {t(`ns.client.${s.client}`)}
                  {osName(s.os) ? ' · ' + osName(s.os) : ''}
                  {s.approx_location ? ' · ' + t('devices.city', { city: s.approx_location }) : ''}
                  {' · '}
                  {t('ns.sessions.lastUsed', { when: fmt.stamp(s.last_used_at) })}
                </div>
              </div>
              <div className="nx-set-ctl">
                {s.current ? (
                  <Btn size="sm" onClick={() => void signOut()}>
                    {t('devices.signOut')}
                  </Btn>
                ) : (
                  <Btn
                    size="sm"
                    kind="quiet"
                    onClick={async () => {
                      try {
                        const done = await withStepUp(async () => {
                          await api.me.revokeSession(s.id);
                          return true;
                        });
                        if (done) reload();
                      } catch (e) {
                        reportError(e);
                      }
                    }}
                  >
                    {t('devices.signOut')}
                  </Btn>
                )}
              </div>
            </div>
          ))
        )}
      </SetGroup>
      <div className="nx-set-acts">
        <Btn
          kind="danger"
          size="sm"
          icon="power"
          disabled={!list || list.length < 2}
          onClick={async () => {
            const ok = await confirmDialog({
              title: t('devices.signOutOthers'),
              body: t('ns.sessions.othersBody'),
              confirmLabel: t('devices.signOut'),
              danger: true,
              icon: 'power',
            });
            if (!ok) return;
            try {
              const done = await withStepUp(async () => {
                await api.me.logoutOthers();
                return true;
              });
              if (!done) return;
              toast(t('ns.sessions.othersDone'), { icon: 'check', tone: 'ok' });
              reload();
            } catch (e) {
              reportError(e);
            }
          }}
        >
          {t('devices.signOutOthers')}
        </Btn>
      </div>
    </>
  );
}
